import React, { useEffect, useState } from 'react';
import moment from 'moment';
import i18n from 'i18n';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { useCallAPI } from 'hooks/useCallAPI';
import { DevicePmpBooking } from 'core/deviceManagement/DeviceManagement';
import {
  DefaultDeviceManagementManager,
  DeviceManagementManager
} from 'core/deviceManagement/DeviceManagementManager';
import { NO_VALUE } from './deviceDetail';
import { DEVICE_LIST_DATE_FORMAT } from './deviceListColumns';
import styles from './deviceManagement.module.scss';

type PmpBookingTableProps = {
  channelId: number;
  unitKey: string;
  startDate: string;
  endDate: string;
  manager?: DeviceManagementManager;
};

const defaultManager = new DefaultDeviceManagementManager();

const formatFlight = (booking: DevicePmpBooking) => {
  if (!booking.startTime || !booking.endTime) {
    return NO_VALUE;
  }
  return `${moment(booking.startTime).format(DEVICE_LIST_DATE_FORMAT)} ~ ${moment(
    booking.endTime
  ).format(DEVICE_LIST_DATE_FORMAT)}`;
};

/**
 * 這個點位在走期內被哪些 PMP 包走。
 *
 * 走期是查詢條件的一部分，所以這一段只掛在可售狀況那一頁的詳情底下 —— 機台清單檢視
 * 沒有走期可帶。
 *
 * 「查不到」與「查壞了」要分得開：兩者都是零列，但使用者該做的事相反（一個是這台真的
 * 沒被包，一個是重試）。
 */
export const PmpBookingTable: React.FC<PmpBookingTableProps> = ({
  channelId,
  unitKey,
  startDate,
  endDate,
  manager = defaultManager
}) => {
  const { loading, callAPIs } = useCallAPI();
  const [bookings, setBookings] = useState<DevicePmpBooking[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    callAPIs(
      [
        () => manager.getPmpBookings({ channelId, unitKey, startDate, endDate })
      ],
      (result: DevicePmpBooking[]) => {
        setFailed(false);
        setBookings(result);
      },
      () => {
        setFailed(true);
        setBookings([]);
      }
    );
  }, [channelId, unitKey, startDate, endDate, callAPIs, manager]);

  return (
    <section className={styles.detailSection}>
      <h2>{i18n.t('deviceManagement.labels.pmpBookings')}</h2>
      {loading && <LoadingIndicator />}
      {failed || bookings.length === 0 ? (
        <div className={styles.calendarNoData} data-testid='pmp-bookings-empty'>
          {i18n.t(
            failed
              ? 'deviceManagement.messages.pmpBookingsLoadFailed'
              : 'deviceManagement.messages.noPmpBooking'
          )}
        </div>
      ) : (
        <table className={styles.pmpBookingTable} data-testid='pmp-bookings'>
          <thead>
            <tr>
              <th>{i18n.t('deviceManagement.headers.orderName')}</th>
              <th>{i18n.t('deviceManagement.headers.pmpFlight')}</th>
              <th>{i18n.t('deviceManagement.headers.agencyName')}</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((booking, index) => (
              <tr key={`${booking.orderName}-${booking.startTime}-${index}`}>
                <td>{booking.orderName || NO_VALUE}</td>
                <td>{formatFlight(booking)}</td>
                <td>{booking.agencyName || NO_VALUE}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};
